import { Link } from 'react-router-dom';
import { useCartActions } from '../../contexts/StoreContext';
import { ProductImage } from '../../ui/ProductImage';
import { isSoldOut } from '../../core/rules/stock';
import { euro } from '../../core/format/currency';
import type { Product } from '../../core/types/catalog';
import { btnPrice, btnPriceAmount, btnPriceLabel } from '../../ui/styles';
import { closingHeading } from '../../ui/sections';
import { AddButton } from '../shared/AddButton';

/**
 * "Werkt het best samen met": dit product en zijn partners als één kleine routine. De nummers zijn
 * de volgorde van gebruik (zo staat het ook in de catalogus), dus ze betekenen iets. Los toevoegen
 * met het plusje, of alles in één keer met de prijs in de knop, net als bij een protocol.
 */
export function ProductPairs({
  product,
  partners
}: {
  product: Product;
  partners: Product[];
}) {
  const {
    add
  } = useCartActions();
  const all = [product, ...partners];
  // Alleen wat leverbaar is gaat in één klik mee; uitverkocht staat er wel, met die melding.
  const available = all.filter(p => !isSoldOut(p));
  const total = available.reduce((n, p) => n + p.price, 0);
  const addAll = () => available.forEach((p, i) => add(p, {
    silent: i < available.length - 1
  }));
  return <section className="bg-shell py-20 lg:py-28" aria-labelledby="pairs-title" data-id="element-8362" data-mp="2s3vn10xjshpa">
      <div className="mx-auto grid max-w-content gap-10 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-8363" data-mp="2x1x3rf7i44xl">
        <div className="lg:col-span-4" data-id="element-8364" data-mp="2jb1tbyhctfrw">
          <h2 id="pairs-title" className={`${closingHeading} text-ink`} data-id="element-8365" data-mp="2j9z19n8ap7ae">
            Werkt het best
            <span className="italic text-clay" data-id="element-8366" data-mp="2o4czdxecdsir"> samen met</span>
          </h2>
          <p className="mt-4 max-w-sm text-[17px] leading-relaxed text-muted" data-id="element-8367" data-mp="29fgto6zrcrlb">Zo geef ik het mee in de salon. Samen versterken ze elkaar; los werkt ook, maar minder snel.</p>
        </div>
        <div className="min-w-0 lg:col-span-8" data-id="element-8368" data-mp="20dhdti0hbtjy">
          <ol className="divide-y divide-line border-y border-line" data-id="element-8369" data-mp="24eb1qbdx98cu">
            {all.map((p, i) => {
            const self = p.id === product.id;
            const soldOut = isSoldOut(p);
            return <li key={p.id} className={`flex items-center gap-3 py-4 sm:gap-4 ${self ? '-mx-3 bg-sand px-3 sm:-mx-4 sm:px-4' : ''}`} data-id="element-8370" data-mp="64tz725mspxc" data-mpi={i}>
                  <span className="w-5 shrink-0 type-name tabular-nums text-clay" aria-hidden="true" data-id="element-8371" data-mp="16adn2uy9g5i">
                    {i + 1}
                  </span>
                  {/* Decoratief: de naam staat ernaast. */}
                  <ProductImage src={p.image} alt="" className="h-20 w-16 shrink-0 bg-blush p-1.5" data-id="element-8372" data-mp="tsbzjm2yowbx" />
                  <div className="min-w-0 flex-1" data-id="element-8373" data-mp="otsc1h8pzchg">
                    <p className="text-[13px] text-clay" data-id="element-8374" data-mp="jvgxwube6ftf">
                      {p.step}
                      {self ? ' · dit product' : ''}
                      {soldOut ? ' · tijdelijk uitverkocht' : ''}
                    </p>
                    {self ? <p className="type-name text-ink" data-id="element-8375" data-mp="30le30raytpf6">{p.name}</p> : <Link to={`/webshop/${p.id}`} className="type-name text-ink transition-colors duration-150 ease-soft hover:text-clay" data-id="element-8376" data-mp="1g33h4qy35l9s">
                        {p.name}
                      </Link>}
                  </div>
                  <span className={`shrink-0 text-[15px] tabular-nums ${soldOut ? 'text-muted line-through decoration-line' : 'text-muted'}`} data-id="element-8377" data-mp="1p304mp8qoe39">{euro(p.price)}</span>
                  {/* Het product zelf voeg je bovenaan toe; hier alleen de partners los. */}
                  {self ? <span className="h-11 w-11 shrink-0" aria-hidden="true" data-id="element-8378" data-mp="23s6agw2312k4" /> : <AddButton product={p} data-id="element-8379" data-mp="23usgusd8lc0r" />}
                </li>;
          })}
          </ol>
          {available.length > 1 && <button type="button" onClick={addAll} className={`${btnPrice} mt-6 sm:w-auto sm:min-w-[22rem]`} data-id="element-8380" data-mp="2ctmaqaz6noam">
              <span className={btnPriceLabel} data-id="element-8381" data-mp="dhbhvitna6qi">{available.length === all.length ? `Alle ${all.length} in de mand` : `De ${available.length} leverbare in de mand`}</span>
              <span className={btnPriceAmount} data-id="element-8382" data-mp="3jaaoexro7rex">{euro(total)}</span>
            </button>}
        </div>
      </div>
    </section>;
}